/**
 * บรรยากาศเคลื่อนไหวของโซน — อนุภาคลอยรอบกล้อง (หิมะ ทราย หิ่งห้อย ประกายไฟ ...)
 *
 * **ของตกแต่งล้วน ไม่มีผลต่อเกม** ไม่จำเป็นต้องตรงกันทุกเครื่อง แต่ยังสุ่มจาก seed คงที่
 * เพื่อให้เทสต์ "ฉากเหมือนกันทุกครั้ง" ใน world.test.ts ยังใช้ได้ และกติกาของ world (ห้าม Math.random) ไม่มีข้อยกเว้น
 *
 * ใช้ instance ของ mesh ต้นแบบชิ้นเดียว: draw call เดียวต่อโซน ไม่ต้องมีไฟล์ texture
 * อนุภาคอยู่ในกล่องรอบกล้อง หลุดขอบแล้ววนกลับอีกฝั่ง จำนวนจึงคงที่ไม่ว่าแผนที่จะกว้างแค่ไหน
 */

import type { Scene } from "@babylonjs/core/scene";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math";
import "@babylonjs/core/Meshes/instancedMesh";

export interface AmbientStyle {
  /** จำนวนอนุภาค */
  count: number;
  color: string;
  size: number;
  /** ความเร็วแนวตั้ง (ลบ = ตก บวก = ลอยขึ้น) หน่วย/วินาที */
  rise: number;
  /** ลมพัดแนวนอน หน่วย/วินาที (แกน x) */
  drift: number;
  /** ส่ายไปมาแรงแค่ไหน */
  wobble: number;
  /** ความสูงต่ำสุด/สูงสุดที่อนุภาคอยู่ */
  minY: number;
  maxY: number;
  /** กะพริบ (หิ่งห้อย ประกาย) */
  twinkle?: boolean;
}

/** ครึ่งความกว้างของกล่องรอบกล้องที่อนุภาคอยู่ */
export const AMBIENT_BOX = 26;

const STYLES: Record<string, AmbientStyle> = {
  meadow: { count: 40, color: "#fff4b0", size: 0.12, rise: 0.15, drift: 0.4, wobble: 0.6, minY: 0.3, maxY: 4, twinkle: true },
  hills: { count: 50, color: "#f3f7e6", size: 0.1, rise: -0.1, drift: 2.2, wobble: 0.5, minY: 0.5, maxY: 6 },
  forest: { count: 70, color: "#d8ff7a", size: 0.13, rise: 0.1, drift: 0.2, wobble: 0.9, minY: 0.3, maxY: 3.5, twinkle: true },
  coast: { count: 40, color: "#ffffff", size: 0.09, rise: 0.05, drift: 1.4, wobble: 0.4, minY: 0.3, maxY: 3 },
  root_cave: { count: 50, color: "#a8ff9c", size: 0.1, rise: 0.2, drift: 0, wobble: 0.5, minY: 0.2, maxY: 4, twinkle: true },
  desert: { count: 90, color: "#f0d9a0", size: 0.08, rise: 0, drift: 3.5, wobble: 0.3, minY: 0.1, maxY: 2.5 },
  dungeon_mine: { count: 50, color: "#c9b28f", size: 0.07, rise: -0.15, drift: 0.1, wobble: 0.3, minY: 0.3, maxY: 5 },
  ruins: { count: 45, color: "#e6e2d0", size: 0.08, rise: -0.1, drift: 0.6, wobble: 0.5, minY: 0.3, maxY: 5 },
  swamp: { count: 70, color: "#c3a0ff", size: 0.12, rise: 0.25, drift: 0.1, wobble: 0.7, minY: 0.2, maxY: 4, twinkle: true },
  crystal_cave: { count: 70, color: "#9ff3ff", size: 0.1, rise: 0.3, drift: 0, wobble: 0.4, minY: 0.2, maxY: 5, twinkle: true },
  snow: { count: 140, color: "#ffffff", size: 0.12, rise: -1.4, drift: 0.7, wobble: 0.6, minY: 0, maxY: 12 },
  dungeon_frost: { count: 90, color: "#dff3ff", size: 0.1, rise: -0.6, drift: 0.2, wobble: 0.5, minY: 0, maxY: 8 },
  volcano: { count: 90, color: "#ff8a3d", size: 0.11, rise: 1.2, drift: 0.4, wobble: 0.6, minY: 0, maxY: 9, twinkle: true },
  storm: { count: 180, color: "#b8c8e0", size: 0.07, rise: -9, drift: 2.5, wobble: 0.1, minY: 0, maxY: 14 },
  jungle: { count: 80, color: "#e8ff6a", size: 0.12, rise: 0.1, drift: 0.1, wobble: 0.8, minY: 0.3, maxY: 4, twinkle: true },
  graveyard: { count: 70, color: "#b8c0c8", size: 0.16, rise: 0.05, drift: 0.5, wobble: 0.3, minY: 0.1, maxY: 1.5 },
  celestial: { count: 80, color: "#ffe9a0", size: 0.11, rise: 0.35, drift: 0.2, wobble: 0.5, minY: 0.2, maxY: 7, twinkle: true },
  abyss_ruins: { count: 70, color: "#b388ff", size: 0.11, rise: 0.4, drift: 0, wobble: 0.8, minY: 0.2, maxY: 6, twinkle: true },
};

/** แบบอนุภาคของโซน ไม่รู้จักโซน = ใช้แบบทุ่งดอกเช้า */
export function ambientStyle(zoneId: string | undefined): AmbientStyle {
  return STYLES[zoneId ?? "meadow"] ?? STYLES["meadow"]!;
}

/** ห่อค่าให้อยู่ในช่วง [center - half, center + half) — หลุดขอบแล้วโผล่อีกฝั่ง */
export function wrapAround(v: number, center: number, half: number): number {
  const span = half * 2;
  let d = (v - center + half) % span;
  if (d < 0) d += span;
  return center - half + d;
}

interface Mote {
  inst: InstancedMesh;
  phase: number;
  speed: number;
}

/**
 * สร้างอนุภาคของโซนแล้วคืนฟังก์ชันล้าง (เรียกตอนเปลี่ยนโซน)
 * ไม่มีกล้อง (เทสต์บางตัว) ก็ยังสร้างได้ ใช้จุดกลางแผนที่แทน
 */
export function startAmbient(scene: Scene, zoneId: string | undefined): () => void {
  const style = ambientStyle(zoneId);
  const base: Mesh = MeshBuilder.CreatePlane("ambient", { size: style.size, sideOrientation: 2 }, scene);
  base.billboardMode = 7; // BILLBOARDMODE_ALL
  const m = new StandardMaterial("ambientMat", scene);
  const c = Color3.FromHexString(style.color);
  m.emissiveColor = c;
  m.diffuseColor = c;
  m.disableLighting = true;
  m.alpha = 0.85;
  m.fogEnabled = false;
  base.material = m;
  base.isPickable = false;
  base.isVisible = false; // ต้นแบบไม่วาด วาดเฉพาะ instance

  const center = (): { x: number; z: number } => {
    const cam = scene.activeCamera as { target?: { x: number; z: number } } | null;
    return cam?.target ?? { x: 0, z: 0 };
  };

  let seed = 4242;
  const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const c0 = center();
  const motes: Mote[] = [];
  for (let i = 0; i < style.count; i++) {
    const inst = base.createInstance(`amb${i}`);
    inst.isPickable = false;
    inst.position.set(
      c0.x + (rnd() * 2 - 1) * AMBIENT_BOX,
      style.minY + rnd() * (style.maxY - style.minY),
      c0.z + (rnd() * 2 - 1) * AMBIENT_BOX,
    );
    motes.push({ inst, phase: rnd() * Math.PI * 2, speed: 0.6 + rnd() * 0.8 });
  }

  let t = 0;
  const obs: Observer<Scene> | null = scene.onBeforeRenderObservable.add(() => {
    const dt = Math.min(0.1, scene.getEngine().getDeltaTime() / 1000);
    t += dt;
    const ctr = center();
    const hY = (style.maxY - style.minY) / 2;
    const cY = style.minY + hY;
    for (const mo of motes) {
      const p = mo.inst.position;
      const w = Math.sin(t * mo.speed * 1.7 + mo.phase) * style.wobble;
      p.x = wrapAround(p.x + (style.drift * mo.speed + w) * dt, ctr.x, AMBIENT_BOX);
      p.z = wrapAround(p.z + Math.cos(t * mo.speed + mo.phase) * style.wobble * dt, ctr.z, AMBIENT_BOX);
      p.y = wrapAround(p.y + style.rise * mo.speed * dt, cY, Math.max(hY, 0.01));
      if (style.twinkle) {
        const s = 0.55 + 0.45 * Math.sin(t * 3 * mo.speed + mo.phase);
        mo.inst.scaling.setAll(s);
      }
    }
  });

  return () => {
    if (obs) scene.onBeforeRenderObservable.remove(obs);
    for (const mo of motes) mo.inst.dispose();
    base.dispose();
    m.dispose();
  };
}
